import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import { VerticalMenuNav } from './_vertical-menu-nav';
import { getHorizontalCollapseLocalStorage } from './_vertical-menu-service';
import { VerticalMenuItemsType } from './vertical-menu-types';
import { Colors } from '@common/tokens';

type Props = {
  items: VerticalMenuItemsType[];
  menuKey: string;
  hideMenu?: boolean;
  sidebarHeader?: ReactNode;
};

const ANIMATION_DURATION_MS = 200;

export function VerticalMenu({
  children,
  menuKey,
  hideMenu,
  items,
  sidebarHeader,
}: PropsWithChildren<Props>) {
  const [isAsideCollapsed, setIsAsideCollapsed] = useState(
    getHorizontalCollapseLocalStorage(menuKey)
  );

  const [hideMenuDelayed, setHideMenuDelayed] = useState(hideMenu);

  useEffect(() => {
    if (hideMenu) {
      setTimeout(() => setHideMenuDelayed(true), ANIMATION_DURATION_MS);
      return;
    }

    setHideMenuDelayed(false);
  }, [hideMenu]);

  return (
    <>
      <div
        className={clsx('wk-vertical-menu', {
          'wk-vertical-menu--aside-collapsed': isAsideCollapsed,
          'wk-vertical-menu--aside-hidden': hideMenu,
        })}
      >
        <aside>
          {!hideMenuDelayed && (
            <VerticalMenuNav
              items={items}
              menuKey={menuKey}
              animationDuration={ANIMATION_DURATION_MS}
              onHorizontalCollapse={setIsAsideCollapsed}
              sidebarHeader={sidebarHeader}
            />
          )}
        </aside>
        <main>{children}</main>
      </div>
      <style jsx>{`
        .wk-vertical-menu {
          height: 100%;
          min-height: 100%;
          max-height: 100%;
          display: grid;
          grid-template-columns: 200px calc(100% - 200px);
          transition: grid-template-columns ${ANIMATION_DURATION_MS}ms linear;
        }

        .wk-vertical-menu aside {
          opacity: 1;
          padding-top: 24px;
          border-right: solid 1px ${Colors.steel};
          transition: opacity ${ANIMATION_DURATION_MS}ms linear;
        }

        .wk-vertical-menu.wk-vertical-menu--aside-hidden aside {
          opacity: 0;
          border-right: none;
        }

        .wk-vertical-menu--aside-collapsed {
          grid-template-columns: 54px calc(100% - 54px);
        }

        .wk-vertical-menu--aside-hidden {
          grid-template-columns: 0 100%;
        }
      `}</style>
    </>
  );
}
